import React, { useState, useEffect, useCallback } from 'react';
import { Layout, MousePointerClick, FileText, Component, CaseSensitive, Sparkles } from 'lucide-react';
import UIComponents from './UIComponents';
import { submitStructuredData } from '../services/LLMCalls';
import { useNavigate } from 'react-router-dom';
import ProvideReference from './ProvideReference';

const LAYOUTS = {
  SINGLE_PAGE: 'single-page',
  LEFT_SIDEBAR: 'left-sidebar',
  RIGHT_SIDEBAR: 'right-sidebar',
  SPLIT_SCREEN: 'split-screen',
  OTHER: 'other'
};

const Structured = ({ projectId, device }) => {
  const [formData, setFormData] = useState({
    title: '',
    layout: LAYOUTS.SINGLE_PAGE,
    otherLayout: '',
    interactivity: '',
    content: '',
    components: [],
    includeTopPanel: false,
    includeBottomPanel: false,
    reference: null // Reference is optional
  });

  const [showLayoutWarning, setShowLayoutWarning] = useState(false);
  const [errors, setErrors] = useState({});
  const [isFormValid, setIsFormValid] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  // Determine if we should show panel options (only for desktop and multi-column layouts)
  const showPanelOptions = 
    device === 'Desktop' && 
    (formData.layout === LAYOUTS.LEFT_SIDEBAR || 
     formData.layout === LAYOUTS.RIGHT_SIDEBAR || 
     formData.layout === LAYOUTS.SPLIT_SCREEN);

  const validateForm = useCallback(() => {
    const newErrors = {};

    // Validate title
    if (!formData.title.trim()) {
      newErrors.title = 'Title is required.';
    } else if (formData.title.length > 100) {
      newErrors.title = 'Title must be 100 characters or less.';
    }

    // Validate otherLayout if 'Other' is selected
    if (formData.layout === LAYOUTS.OTHER) {
      if (!formData.otherLayout.trim()) {
        newErrors.otherLayout = 'Please describe your layout.';
      } else if (formData.otherLayout.length > 250) {
        newErrors.otherLayout = 'Layout description must be 250 characters or less.';
      }
    }

    // Validate interactivity
    if (formData.interactivity.length > 1500) {
      newErrors.interactivity = 'Interactivity must be 1500 characters or less.';
    }

    // Validate content
    if (formData.content.length > 10000) {
      newErrors.content = 'Content must be 10000 characters or less.';
    }

    // Validate components from UIComponents
    const requiredSections = getRequiredSections(formData.layout, formData.includeTopPanel, formData.includeBottomPanel);
    
    // Only add a components error if at least one required section has no components
    let missingComponents = false;
    
    // Check if each required section has at least one component
    requiredSections.forEach(sectionKey => {
      const sectionComponents = formData.components.filter(comp => comp.section === sectionKey);
      if (sectionComponents.length === 0) {
        missingComponents = true;
      }
    });
    
    if (missingComponents) {
      newErrors.components = 'Please add at least one section to each area.';
    }

    return newErrors;
  }, [formData]);

  useEffect(() => {
    const validationErrors = validateForm();
    setErrors(validationErrors);
    setIsFormValid(Object.keys(validationErrors).length === 0);
  }, [validateForm]);

  const handleLayoutChange = (e) => {
    const newLayout = e.target.value;
    if (formData.layout !== newLayout && formData.components.length > 0) {
      setShowLayoutWarning(true);
    }
    
    // When layout changes, reset panel options and components
    setFormData(prev => ({
      ...prev,
      layout: newLayout,
      includeTopPanel: false,
      includeBottomPanel: false,
      components: [] // Reset components when layout changes
    }));
  };

  // Updated function to include top and bottom panels
  const getRequiredSections = (layout, includeTopPanel, includeBottomPanel) => {
    let sections = [];
    
    // Add top panel if selected
    if (includeTopPanel) {
      sections.push('top');
    }
    
    // Add main layout sections
    switch (layout) {
      case LAYOUTS.SINGLE_PAGE:
        sections.push('main');
        break;
      case LAYOUTS.LEFT_SIDEBAR:
        sections.push('sidebar', 'main');
        break;
      case LAYOUTS.RIGHT_SIDEBAR:
        sections.push('main', 'sidebar');
        break;
      case LAYOUTS.SPLIT_SCREEN:
        sections.push('left', 'right');
        break;
      default:
        sections.push('main');
        break;
    }
    
    // Add bottom panel if selected
    if (includeBottomPanel) {
      sections.push('bottom');
    }
    
    return sections;
  };

  const handlePanelChange = (e) => {
    const { name, checked } = e.target;
    
    // Update panel state
    setFormData(prev => ({
      ...prev,
      [name]: checked
    }));
    
    // If a panel is being unchecked, remove its components
    if (!checked) {
      const panelType = name === 'includeTopPanel' ? 'top' : 'bottom';
      setFormData(prev => ({
        ...prev,
        components: prev.components.filter(comp => comp.section !== panelType)
      }));
    }
  };

  const handleReferenceChange = (reference) => {
    setFormData(prev => ({
      ...prev,
      reference
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitted(true);
    
    // If not valid and not showing errors yet, add errors now
    const validationErrors = validateForm();
    setErrors(validationErrors);
    
    if (isFormValid) {
      setIsSubmitting(true);
      try {
        // Navigate to status page immediately
        navigate('/prompt-status', {
          state: {
            projectId,
            isProcessing: true
          }
        });

        // Make API call after navigation
        const response = await submitStructuredData(projectId, formData);

        // Update status page with promptId
        navigate('/prompt-status', {
          state: {
            projectId,
            promptId: response.promptId,
            isProcessing: true
          },
          replace: true
        });
      } catch (error) {
        console.error('Error:', error);
        navigate('/prompt-status', {
          state: {
            projectId,
            error: 'Failed to submit instructions. Please try again.'
          },
          replace: true
        });
      } finally {
        setIsSubmitting(false);
      }
    } else {
      console.error('Form is invalid:', validationErrors);
    }
  };

  return (
    <form onSubmit={handleSubmit} className='space-y-8'>
      {/* Title Section */}
      <div className='space-y-4'>
        <div className='space-y-2'>
          <div className='flex items-center gap-3 bg-gradient-to-br from-gray-100 to-gray-50 rounded-lg p-7 shadow-md mb-4'>
            <div className='flex items-center justify-center w-7 h-7'>
              <CaseSensitive className='w-5 h-5 text-gray-600' />
            </div>
            <div>
              <label className='text-lg font-semibold text-gray-800'>
                Title
              </label>
              <p className='text-gray-500'>Enter a title for the view you would like to generate.</p>
            </div>
          </div>
        </div>
        <div>
          <input
            type='text'
            name='title'
            value={formData.title}
            onChange={(e) =>
              setFormData(prev => ({ ...prev, title: e.target.value }))
            }
            placeholder='Descriptive title for your view...'
            className='w-full p-3 border rounded-md'
          />
          {isSubmitted && errors.title && <p className='text-orange-500 text-sm'>{errors.title}</p>}
        </div>
      </div>

      {/* Layout Section */}
      {device === 'Desktop' && (
        <div className='space-y-4'>
          <div className='space-y-2'>
            <div className='flex items-center gap-3 bg-gradient-to-br from-gray-100 to-gray-50 rounded-lg p-7 shadow-md mb-4'>
              <div className='flex items-center justify-center w-7 h-7'>
                <Layout className='w-5 h-5 text-gray-600' />
              </div>
              <div>
                <label className='text-lg font-semibold text-gray-800'>
                  Layout
                </label>
                <p className='text-gray-500'>Choose a layout for the view that you would like to generate.</p>
              </div>
            </div>
          </div>

          <select
            value={formData.layout}
            onChange={handleLayoutChange}
            className='w-full p-2 border rounded-md bg-white'
          >
            {Object.entries(LAYOUTS).map(([key, value]) => (
              <option key={value} value={value}>
                {value.replace('-', ' ').replace(/\b\w/g, c => c.toUpperCase())}
              </option>
            ))}
          </select>

          {/* Panel options for multi-column layouts */}
          {showPanelOptions && (
            <div className="flex items-center space-x-6 mt-2">
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="includeTopPanel"
                  name="includeTopPanel"
                  checked={formData.includeTopPanel}
                  onChange={handlePanelChange}
                  className="w-4 h-4"
                />
                <label htmlFor="includeTopPanel" className="text-gray-700">
                  Include top panel
                </label>
              </div>
              
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="includeBottomPanel"
                  name="includeBottomPanel"
                  checked={formData.includeBottomPanel}
                  onChange={handlePanelChange}
                  className="w-4 h-4"
                />
                <label htmlFor="includeBottomPanel" className="text-gray-700">
                  Include bottom panel
                </label>
              </div>
            </div>
          )}

          {formData.layout === LAYOUTS.OTHER && (
            <div>
              <input
                type='text'
                value={formData.otherLayout}
                onChange={(e) =>
                  setFormData(prev => ({ ...prev, otherLayout: e.target.value }))
                }
                placeholder='Describe your layout'
                className='w-full p-3 border rounded-md'
              />
              {isSubmitted && errors.otherLayout && <p className='text-orange-500 text-sm'>{errors.otherLayout}</p>}
            </div>
          )}

          {showLayoutWarning && (
            <p className='text-gray-600 text-sm'>
              Changing layout refreshes all UI element selections.
            </p>
          )}
        </div>
      )}

      {/* UI Elements Section */}
      <div className='space-y-4'>
        <div className='space-y-2'>
          <div className='flex items-center gap-3 bg-gradient-to-br from-gray-100 to-gray-50 rounded-lg p-7 shadow-md mb-4'>
            <div className='flex items-center justify-center w-7 h-7'>
              <Component className='w-5 h-5 text-gray-600' />
            </div>
            <div>
              <label className='text-lg font-semibold text-gray-800'>
                UI Elements
              </label>
              <p className='text-gray-500'>Add sections to your view from top to bottom. Move them freely up or down.</p>
            </div>
          </div>
        </div>
      </div>

      <UIComponents
        layout={formData.layout}
        includeTopPanel={formData.includeTopPanel}
        includeBottomPanel={formData.includeBottomPanel}
        components={formData.components}
        setComponents={(newComponents) =>
          setFormData(prev => ({ ...prev, components: newComponents }))
        }
      />
      {isSubmitted && errors.components && <p className='text-orange-500 text-sm'>{errors.components}</p>}

      {/* Interactivity Section */}
      <div className='space-y-4'>
        <div className='space-y-2'>
          <div className='flex items-center gap-3 bg-gradient-to-br from-gray-100 to-gray-50 rounded-lg p-7 shadow-md mb-4'>
            <div className='flex items-center justify-center w-7 h-7'>
              <MousePointerClick className='w-5 h-5 text-gray-600' />
            </div>
            <div>
              <label className='text-lg font-semibold text-gray-800'>
                Interactivity
              </label>
              <p className='text-gray-500'>Describe how you want the users to interact with the view.</p>
            </div>
          </div>
          <textarea
            value={formData.interactivity}
            onChange={(e) =>
              setFormData(prev => ({ ...prev, interactivity: e.target.value }))
            }
            placeholder='[OPTIONAL] Describe desired user behavior. Users can click buttons to expand sections, hover over cards to see details, and drag elements to rearrange them...'
            className='w-full p-3 border rounded-md min-h-[100px]'
          />
          {errors.interactivity && <p className='text-orange-500 text-sm'>{errors.interactivity}</p>}
        </div>

        {/* Content Section */}
        <div className='space-y-2'>
          <div className='flex items-center gap-3 bg-gradient-to-br from-gray-100 to-gray-50 rounded-lg p-7 shadow-md mb-4'>
            <div className='flex items-center justify-center w-7 h-7'>
              <FileText className='w-5 h-5 text-gray-600' />
            </div>
            <div>
              <label className='text-lg font-semibold text-gray-900'>
                Content
              </label>
              <p className='text-gray-600'>Provide clear guidance or specific details about the content you want in the view.</p>
            </div>
          </div>
          <textarea
            value={formData.content}
            onChange={(e) =>
              setFormData(prev => ({ ...prev, content: e.target.value }))
            }
            placeholder='[OPTIONAL] Provide content or include high-level instructions for the AI to generate content. Describe content themes, structure, or tone, as well as specific elements like titles, labels, text...'
            className='w-full p-3 border rounded-md min-h-[100px]'
          />
          {errors.content && <p className='text-orange-500 text-sm'>{errors.content}</p>}
        </div>
      </div>

      {/* ProvideReference Component */}
      <ProvideReference 
        projectId={projectId}
        onReferenceChange={handleReferenceChange}
      />

      {/* Next Step Button */}
      <div className='flex justify-end'>
        <button
          type='submit'
          disabled={!isFormValid || isSubmitting}
          className={`flex items-center gap-2 px-6 py-3 bg-black text-white rounded-md hover:bg-gray-800 disabled:bg-gray-400 disabled:cursor-not-allowed`}
        >
          {isSubmitting ? (
            <div className='animate-spin rounded-full h-5 w-5 border-b-2 border-white'></div>
          ) : (
            <Sparkles className='w-5 h-5' />
          )}
          {isSubmitting ? 'Processing...' : 'Next Step'}
        </button>
      </div>
    </form>
  );
};

export default Structured;